import type { BloomThemeScore } from "../types/bloom-content";
import gardenBg from "../assets/garden/garden-background.webp";
import { GARDEN_SILHOUETTES } from "../lib/garden-silhouettes.generated";
import {
  THEME_ASSET_PREFIX,
  THEME_GARDEN_SLOTS,
  gardenModalTitle,
  getGardenAssetUrl,
  tierForThemeScore,
  type ThemeGardenSlot,
} from "../lib/results-garden";
import { GARDEN_TIER_SIZES } from "../lib/garden-tier-sizes";

interface ResultsGardenProps {
  scores: BloomThemeScore[];
  tierOverrides?: Record<string, number>;
  slotOverrides?: Record<string, Partial<ThemeGardenSlot>>;
  highlightThemeId?: string | null;
  onSelectTheme: (themeId: string) => void;
}

export function ResultsGarden({ scores, tierOverrides, slotOverrides, highlightThemeId, onSelectTheme }: ResultsGardenProps) {
  const scoreByTheme = new Map(scores.map((s) => [s.themeId, s]));

  return (
    <div className="bloom-results-garden">
      <svg className="bloom-results-garden__defs" aria-hidden="true" focusable="false">
        {Object.entries(GARDEN_SILHOUETTES).map(([key, d]) => (
          <clipPath key={key} id={`bloom-clip-${key}`} clipPathUnits="objectBoundingBox">
            <path d={d} />
          </clipPath>
        ))}
      </svg>
      <div className="bloom-results-garden__frame">
        <p className="bloom-results-garden__hint">
          Klik op een object in je tuin voor uitleg.
        </p>
        <div className="bloom-results-garden__scene">
          <img
            className="bloom-results-garden__bg"
            src={gardenBg}
            alt=""
            aria-hidden="true"
          />
          {THEME_GARDEN_SLOTS.map((baseSlot) => {
            const score = scoreByTheme.get(baseSlot.themeId);
            if (!score) return null;

            const prefix = THEME_ASSET_PREFIX[baseSlot.themeId];
            const tier = tierOverrides?.[baseSlot.themeId] ?? tierForThemeScore(score);
            const tierSize = GARDEN_TIER_SIZES[`${baseSlot.themeId}:${tier}`];
            const slot: ThemeGardenSlot = {
              ...baseSlot,
              ...tierSize,
              ...slotOverrides?.[baseSlot.themeId],
            };
            const src = prefix ? getGardenAssetUrl(prefix, tier) : undefined;
            if (!src) return null;

            const clipKey = `${prefix}-${tier}`;

            return (
              <button
                key={slot.themeId}
                type="button"
                className={`bloom-results-garden__object${
                  highlightThemeId === slot.themeId ? " bloom-results-garden__object--highlight" : ""
                }`}
                style={{
                  left: `${slot.left}%`,
                  bottom: `${slot.bottom}%`,
                  zIndex: slot.zIndex,
                  height: `${slot.maxHeightPercent}%`,
                }}
                aria-label={`${gardenModalTitle(score.themeId, score.label)}: ${score.score} van ${score.maxScore}`}
                onClick={() => onSelectTheme(slot.themeId)}
              >
                <img className="bloom-results-garden__object-img" src={src} alt="" />
                <span
                  className="bloom-results-garden__object-hit"
                  style={{ clipPath: `url(#bloom-clip-${clipKey})` }}
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
